import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../firebase';
import { UserSelection, LeaderboardEntry } from '../types';

interface TeamData {
  points: number;
  bonusPoints: number;
}

const REGULAR_MULTIPLIERS: number[] = [1.5, 1.2, 1.2];

const Rank: React.FC = () => {
  const [rankings, setRankings] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch teams data
        const teamsRef = ref(db, 'teams');
        const teamsSnapshot = await get(teamsRef);
        if (!teamsSnapshot.exists()) {
          throw new Error('No teams data found');
        }
        const teamsData: { [key: string]: TeamData } = teamsSnapshot.val();

        // Fetch selections data
        const selectionsRef = ref(db, 'selections');
        const selectionsSnapshot = await get(selectionsRef);
        if (!selectionsSnapshot.exists()) {
          throw new Error('No selections data found');
        }
        const selectionsData: { [key: string]: UserSelection } = selectionsSnapshot.val();

        // Calculate points and create rankings
        const rankingsData: LeaderboardEntry[] = Object.values(selectionsData).map(selection => {
          // Calculate regular teams points with multipliers
          const regularPoints = selection.selections.regular.reduce((total, team, index) => {
            const teamData = teamsData[team];
            if (teamData) {
              return total + ((teamData.points + teamData.bonusPoints) * REGULAR_MULTIPLIERS[index]);
            }
            return total;
          }, 0);

          // Get Love team data
          const loveTeamData = teamsData[selection.selections.love];
          const loveTeamBonus = loveTeamData?.bonusPoints > 0;
          const peaceAndLoveMultiplier = loveTeamBonus ? 2.0 : 1.0;

          // Calculate Peace and Love team points
          const peaceTeamData = teamsData[selection.selections.peace];
          const peacePoints = peaceTeamData ? 
            (peaceTeamData.points + peaceTeamData.bonusPoints) * peaceAndLoveMultiplier : 0;

          const lovePoints = loveTeamData ? 
            (loveTeamData.points + loveTeamData.bonusPoints) : 0;

          // Total points
          const totalPoints = regularPoints + peacePoints + lovePoints;

          return {
            userId: selection.userId,
            totalPoints,
            selections: selection.selections
          };
        });

        // Sort rankings by points (descending)
        rankingsData.sort((a, b) => b.totalPoints - a.totalPoints);
        setRankings(rankingsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-primary mb-8">Rankings</h1>
      
      <div className="overflow-x-auto shadow-lg rounded-lg">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Player ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Regular Teams (1.5×, 1.2×, 1.2×)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Love Team
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Peace Team
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Points
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rankings.map((ranking, index) => (
              <tr key={ranking.userId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {index + 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {ranking.userId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {ranking.selections.regular.join(', ')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {ranking.selections.love}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {ranking.selections.peace}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                  {ranking.totalPoints.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Rank; 